import { can, scopeSetFromPermissions } from "../../hooks/useEffectivePermissions.js";
import { faqCategoriesForSegment } from "./faqContent.js";

/** Contexto de visibilidade da Central de Ajuda para o usuário logado. */
export function buildHelpUserAccess({ userData, permissionsData } = {}) {
  const scopeSet = scopeSetFromPermissions(permissionsData, userData);
  const isMaster = Number(userData?.master) === 1;
  const isPlatformAdmin = isMaster || String(userData?.scope || "").trim() === "platform";
  const isPartner = String(userData?.scope || "").trim() === "partner";

  return {
    scopeSet,
    isMaster,
    isPlatformAdmin,
    isPartner,
    canViewBilling: can(scopeSet, "billing", "view"),
    canViewTimeClock: can(scopeSet, "time_clock", "view"),
  };
}

export function parseCsvPermission(raw) {
  const value = String(raw || "").trim();
  if (!value) return null;
  const [resource, action = "view"] = value.split(":");
  if (!resource) return null;
  return { resource: resource.trim(), action: action.trim() || "view" };
}

export function navDefMatchesPermissions(def, access) {
  if (!def?.res) return true;
  return can(access?.scopeSet, def.res, "view");
}

export function featureItemMatchesPermissions(item, access) {
  const parsed = parseCsvPermission(item?.permissions);
  if (parsed) return can(access?.scopeSet, parsed.resource, parsed.action);
  return true;
}

/** Superfícies do inventário visíveis conforme o perfil. */
export function helpSurfaceVisibleToUser(surface, access) {
  const key = String(surface || "app").trim();
  if (!key || key === "app") return true;
  if (key === "platform_console") return Boolean(access?.isPlatformAdmin);
  if (key === "voice_sms") return can(access?.scopeSet, "channels", "view");
  if (key === "public_ecom") {
    return (
      can(access?.scopeSet, "payment_methods", "view") ||
      can(access?.scopeSet, "delivery_methods", "view") ||
      can(access?.scopeSet, "products", "view")
    );
  }
  if (key === "public_websites") return can(access?.scopeSet, "contents", "view");
  if (key === "public_surveys") return can(access?.scopeSet, "surveys", "view");
  return false;
}

export function faqCategoryMatchesAccess(category, access) {
  const audience = String(category?.audience || "").trim();
  if (audience === "billing") return Boolean(access?.canViewBilling || access?.isMaster);
  if (audience === "platform") return Boolean(access?.isPlatformAdmin);
  if (audience === "partner") return Boolean(access?.isPartner);
  return true;
}

export function faqItemMatchesAccess(item, access) {
  const audience = String(item?.audience || "").trim();
  if (audience === "billing") return Boolean(access?.canViewBilling || access?.isMaster);
  if (audience === "platform") return Boolean(access?.isPlatformAdmin);
  if (audience === "partner") return Boolean(access?.isPartner);

  if (Array.isArray(item?.requiredPermission) && item.requiredPermission.length >= 1) {
    const [resource, action = "view"] = item.requiredPermission;
    return can(access?.scopeSet, resource, action);
  }

  return true;
}

export function topbarHelpItemVisible(item, access, ctx = {}) {
  if (typeof item?.visible === "function") {
    return item.visible({
      canViewBilling: access?.canViewBilling,
      canViewTimeClock: access?.canViewTimeClock,
      isMasterUser: access?.isMaster,
      isPlatformAdmin: access?.isPlatformAdmin,
      isPartnerUser: access?.isPartner,
      ...ctx,
    });
  }
  return true;
}

export function filterTopbarHelpItems(items, access) {
  return (items || [])
    .filter((it) => topbarHelpItemVisible(it, access))
    .map((it) => {
      if (it.kind !== "group" || !Array.isArray(it.children)) return it;
      return {
        ...it,
        children: it.children.filter((ch) => topbarHelpItemVisible(ch, access)),
      };
    })
    .filter((it) => it.kind !== "group" || (it.children || []).length > 0);
}

export function faqCategoriesForUser(segment, access) {
  return faqCategoriesForSegment(segment)
    .filter((cat) => faqCategoryMatchesAccess(cat, access))
    .map((cat) => ({
      ...cat,
      items: cat.items.filter((item) => faqItemMatchesAccess(item, access)),
    }))
    .filter((cat) => cat.items.length > 0);
}

export function guideTopicMatchesAccess(topic, access) {
  const audience = String(topic?.audience || "").trim();
  if (audience === "platform") return Boolean(access?.isPlatformAdmin);
  if (audience === "partner") return Boolean(access?.isPartner);
  if (audience === "billing") return Boolean(access?.canViewBilling || access?.isMaster);

  if (Array.isArray(topic?.requiredPermission) && topic.requiredPermission.length >= 1) {
    const [resource, action = "view"] = topic.requiredPermission;
    return can(access?.scopeSet, resource, action);
  }

  if (topic?.resource) return can(access?.scopeSet, topic.resource, "view");
  return true;
}
